/**
 * Experiments Context Provider and Hook
 *
 * Manages experiment variant tracking for multiple concurrent experiments
 * and reports them to analytics.
 *
 * ## Data Layer Format
 *
 * Experiments are reported to the data layer as a single delimited string:
 * `experiments: "experiment_id:variant_value:version:percentage|experiment_id:variant_value:version:percentage"`
 *
 * Example:
 * `experiments: "search_ranking:relevance:v2:p20|checkout_flow:single_page:v1:p50"`
 *
 * This format:
 * - Supports unlimited concurrent experiments (no GA4 dimension limits)
 * - Is easy to parse in BigQuery: SPLIT(experiments, '|') then SPLIT(item, ':')
 * - Keeps event payload small by using IDs instead of verbose names
 * - Maintains historical compatibility when experiments are added/removed
 *
 * ## Usage
 *
 * ```typescript
 * const { searchRanking, checkoutFlow } = useExperiments();
 *
 * if (searchRanking.value === 'relevance') {
 *   // Use treatment variant
 * }
 *
 * // Access experiment config if needed
 * if (searchRanking.experiment.version >= 2) {
 *   // Version-specific logic
 * }
 * ```
 *
 * ## Adding New Experiments
 *
 * 1. Add to ALL_EXPERIMENTS array in `@config/experiments`
 * 2. Use the hook (experiment IDs are converted to camelCase)
 */

import * as React from 'react';
import { getExperimentVariant } from '@utils/experiment-bucketing';
import { ACTIVE_EXPERIMENTS, ExperimentConfig, ExperimentVariant } from '@config/experiments';
import { ExperimentsPanelMount } from '@components/experiments-panel';
import { useAnalytics } from './use-analytics';

/**
 * Assigned variant with experiment config attached
 * Primary use: variant.value, variant.description, variant.percentage
 * Secondary use: variant.experiment.* (config if needed)
 */
export type AssignedVariant = ExperimentVariant & {
  experiment: ExperimentConfig;
};

/**
 * Experiments context with camelCase keys for idiomatic TypeScript
 * Each property is the assigned variant for that experiment
 */
export type ExperimentsContext = {
  [key: string]: AssignedVariant;
};
const ExperimentsContext = React.createContext<ExperimentsContext | undefined>(undefined);
interface Props {
  children: React.ReactNode;
}
export const ExperimentsProvider: React.FC<Props> = ({
  children
}) => {
  const {
    setCurrentExperiments
  } = useAnalytics();

  // Track initialization state and variant IDs for all experiments
  const [experimentStates, setExperimentStates] = React.useState<Map<string, string>>(new Map());
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Initialize all experiments on mount
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const states = new Map<string, string>();
      ACTIVE_EXPERIMENTS.forEach(experiment => {
        const variantId = getExperimentVariant(undefined, experiment);
        states.set(experiment.id, variantId);
      });
      setExperimentStates(states);
      setIsInitialized(true);
    }
  }, []);

  // Convert snake_case to camelCase for idiomatic TypeScript
  const toCamelCase = (str: string): string => {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  // Build experiment data for each active experiment
  const experimentsData = React.useMemo<ExperimentsContext>(() => {
    const data: ExperimentsContext = {};
    ACTIVE_EXPERIMENTS.forEach(experiment => {
      const variantValue = experimentStates.get(experiment.id) || experiment.variants[0].value;
      const variant = experiment.variants.find(v => v.value === variantValue) || experiment.variants[0];

      // Use camelCase key and flatten structure
      const key = toCamelCase(experiment.id);
      data[key] = {
        ...variant,
        experiment
      };
    });
    return data;
  }, [experimentStates]);

  // Report to analytics when experiments are initialized
  React.useEffect(() => {
    if (!isInitialized) return;

    // Build delimited string: "experiment_id:variant_id:version:percentage|..."
    const experimentStrings: string[] = [];
    ACTIVE_EXPERIMENTS.forEach(experiment => {
      const variantValue = experimentStates.get(experiment.id);
      const variant = experiment.variants.find(v => v.value === variantValue);
      if (variant) {
        experimentStrings.push(`${experiment.id}:${variant.value}:v${experiment.version}:p${variant.percentage}`);
      }
    });
    const experimentsString = experimentStrings.join('|');
    setCurrentExperiments(experimentsString || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, experimentStates]);
  return <ExperimentsContext.Provider value={experimentsData}>
      {children}
      {/*
        Mounted here rather than in each provider wrapper so it follows the experiments
        themselves — every route group that has experiments gets the panel, and the two
        can't drift apart. Renders nothing until its hotkey fires.
       */}
      <ExperimentsPanelMount />
    </ExperimentsContext.Provider>;
};
export const useExperiments = () => {
  const context = React.useContext(ExperimentsContext);
  if (context === undefined) {
    throw new Error("useExperiments must be used within an ExperimentsProvider");
  }
  return context;
};